import { Navigate, useParams } from "react-router";

import { isClinicalSummarySectionSlug } from "@/api/hooks";
import { isLibrarySectionSlug } from "@/lib/librarySections";
import { PageHeader } from "@/shell/PageHeader";
import { SectionRail } from "@/shell/SectionRail";
import { findSection, type SectionGroup } from "@/shell/navModel";

import { ProjectQuestionnaires } from "@/ui/adoptions/ProjectQuestionnaires";
import { LibraryCatalog } from "@/ui/library/LibraryCatalog";
import { BackgroundCatalog } from "@/ui/library/named/BackgroundCatalog";
import { ClosingCatalog } from "@/ui/library/named/ClosingCatalog";
import { MainConcernCatalog } from "@/ui/library/named/MainConcernCatalog";
import { PathwayCatalog } from "@/ui/library/named/PathwayCatalog";
import { SummaryCatalog } from "@/ui/library/named/SummaryCatalog";
import { ProjectClosingConversation } from "@/ui/project/ProjectClosingConversation";
import { ClinicalSummary } from "@/ui/summary/ClinicalSummary";
import { SectionDiagnostics } from "@/ui/validation/SectionDiagnostics";
import { UnavailableState } from "@/ui/states";

/**
 * Rail plus section body, shared by Clinical configuration and the library.
 *
 * Issue #4 establishes these routes and their slugs so the editors that arrive
 * in #32–#36 slot in without renaming anything. Issue #7 adds the first real
 * body: for a clinical-configuration section it renders the effective
 * projection read-only and the validation diagnostics joined to it. #32 adds
 * the library's: each library section renders its slice of the shared
 * clinical-library catalog. #373 adds the first slug-specific one: `summary`
 * renders the Clinical Summary instead of the generic diagnostics, because the
 * summary is the report a clinician signs and what the project has adopted
 * changes what it does — neither of which the generic body can say. #489 adds
 * the second: `questionnaires`, whose body is #369's project-scoped
 * questionnaire adoption detail. That one is not merely slug-specific but
 * slug-ONLY — `questionnaires` is not a `SectionSlug`, so it has no projection
 * to fall back to and must be intercepted before the guard below. Every other
 * clinical slug is untouched.
 *
 * #516 removed the in-place default resolution this surface carried for the
 * Project root. See the note on the `section` lookup below.
 *
 * #908 gives the library half a shared HEADER as well as a shared rail. The
 * page-wide "Clinical library" banner `LibraryRoute` used to render above this
 * surface is gone, so each library body opens with its own
 * `LibrarySectionHeader`: the breadcrumb, the section's name as the page's `h1`,
 * the description declared beside its rail entry, and that section's creation
 * action. The description is handed DOWN from here rather than held in each
 * catalog, because the generic `LibraryCatalog` serves five sections from one
 * component. `surface` reaches the rail too now, which is what draws the
 * library's unlabelled, accent-selected rail and the clinical rail's
 * collapsible groups from one component without either imitating the other.
 *
 * `projectId` opts a mounting route in, and `surface` says which contract the
 * slugs belong to. The two surfaces share a rail and nothing else: clinical
 * slugs are members of `SectionSlug` and fetch `getProjectSection`; library
 * slugs are members of `LibrarySectionSlug` and read the content-free catalog
 * list. Sending `questionnaires` to `getProjectSection` is the mistake this
 * guard exists to make impossible.
 */
export function SectionSurface({
  groups,
  basePath,
  railLabel,
  defaultSlug,
  emptyHeading,
  projectId,
  surface = "library",
}: {
  groups: SectionGroup[];
  basePath: string;
  railLabel: string;
  defaultSlug: string;
  emptyHeading: string;
  /** Set by Clinical configuration, not by the library. */
  projectId?: string;
  surface?: "clinical" | "library";
}) {
  // A bare parent address — `/config` or `/library` — has no meaning of its
  // own, so it redirects to the default section rather than answering in place.
  //
  // #516 removed the `resolveDefaultInPlace` opt-out that stood here. #203 used
  // it to render the default section AT the Project root without rewriting
  // `/projects/:projectId`, and that root now renders the Overview instead, so
  // this surface has no caller that mounts it without a `:section` it means to
  // resolve.
  const { section } = useParams();

  if (!section) {
    return <Navigate to={`${basePath}/${defaultSlug}`} replace />;
  }

  const match = findSection(groups, section);

  // The section's own description, declared beside its rail entry (#908). Every
  // library entry carries one; the clinical-configuration entries carry none,
  // because a Project section is titled by the Project shell rather than by a
  // header of its own.
  const description = match?.description ?? "";

  return (
    <div className="flex flex-col gap-6 md:flex-row">
      <SectionRail
        groups={groups}
        basePath={basePath}
        ariaLabel={railLabel}
        surface={surface}
      />

      <div className="min-w-0 flex-1">
        {!match ? (
          <>
            {/*
              A HEADING FOR THE LIBRARY'S UNKNOWN SECTION (#908).

              `UnavailableState` states the problem in a `<p>`, not a heading —
              correct on the clinical surface, where the Project shell above it
              already owns the page's `h1`. The library's `h1` used to be
              `LibraryRoute`'s page-wide banner, and removing it left a stale or
              mistyped `/library/<slug>` as the one library address with no page
              heading at all. So the library half renders its own, by the same
              component every known section uses; the clinical half is unchanged
              and would otherwise gain a second competing `h1`.
            */}
            {surface === "library" ? (
              <PageHeader
                breadcrumbs={[
                  { label: "Clinical library", to: "/library" },
                  { label: "Unknown section" },
                ]}
                title="Unknown section"
              />
            ) : null}
            <UnavailableState
              title="Unknown section"
              description="No section with that name exists in this workspace."
              blockedBy={`Known sections: ${groups
                .flatMap((group) => group.items.map((item) => item.slug))
                .join(", ")}.`}
            />
          </>
        ) : projectId && surface === "clinical" && section === "summary" ? (
          <ClinicalSummary projectId={projectId} sectionLabel={match.label} />
        ) : projectId &&
          surface === "clinical" &&
          section === "questionnaires" ? (
          // #489's second slug-specific body, by the SAME mechanism as
          // `summary` above and for a stricter reason: `questionnaires` is not a
          // `SectionSlug` at all, so it must never reach the `isSectionSlug`
          // branch below. The rail entry declares `projection: "none"` and
          // `src/invariants/routes.test.ts` asserts that such an entry can never
          // be a contract slug, so this branch and that guard fail together
          // rather than one of them quietly widening.
          <ProjectQuestionnaires
            projectId={projectId}
            sectionLabel={match.label}
          />
        ) : projectId && surface === "clinical" && section === "closing" ? (
          // #867's slug-specific body, by the SAME mechanism as `questionnaires`
          // above and for the same strict reason: `closing` is not a
          // `SectionSlug` at all, so it must never reach the `isSectionSlug`
          // branch below. The rail entry declares `projection: "none"` and
          // `src/invariants/routes.test.ts` asserts that such an entry can never
          // be a contract slug, so this branch and that guard fail together
          // rather than one of them quietly widening.
          <ProjectClosingConversation
            projectId={projectId}
            sectionLabel={match.label}
          />
        ) : projectId &&
          surface === "clinical" &&
          // #488 narrows this from `isSectionSlug` to the six slugs that have a
          // bounded review contract. `summary` IS a `SectionSlug` but has no
          // summary contract — the service answers 422 for it — and it is
          // already intercepted above. Narrowing here makes that interception a
          // property of the type rather than of the order these branches happen
          // to be written in.
          isClinicalSummarySectionSlug(section) ? (
          <SectionDiagnostics
            projectId={projectId}
            section={section}
            sectionLabel={match.label}
          />
        ) : surface === "library" && section === "pathways" ? (
          // #643's library slug-specific body, by the SAME mechanism as
          // `chief-complaint` below. The generic catalog lists `pathway` and
          // `pathway_variant` as a flat table of typed assets, which is the
          // state the parity audit recorded as PW-LIB-01; the ratified contract
          // closed that gate in favour of the explicit family/default model, and
          // `GET /library/pathway-families` is the read that serves it. The
          // generic catalog structurally cannot render it — a family is a group
          // with its own parent identity, and catalog reads carry no steps.
          <PathwayCatalog
            sectionLabel={match.label}
            sectionDescription={description}
          />
        ) : surface === "library" && section === "bg-sets" ? (
          // #859's library slug-specific body, by the SAME mechanism as
          // `pathways` above. The generic catalog lists background sets as one
          // undifferentiated table; the reference separates `Set files` from
          // `Domains`, gives each group its own creation action, and shows what
          // a domain IS — its runtime id, its interview position and its prompt
          // count — all of which is CONTENT, and catalog reads carry none by
          // contract.
          <BackgroundCatalog
            sectionLabel={match.label}
            sectionDescription={description}
          />
        ) : surface === "library" && section === "closing" ? (
          // #867's library slug-specific body, by the SAME mechanism as
          // `pathways` and `bg-sets` above. The generic catalog offers
          // `NewAssetDialog`, whose free-text "Part key" input and raw-JSON
          // structured field are exactly the arbitrary file editor the ratified
          // S7 screen contract forbids for this family — #866 routed the family
          // there deliberately and named this leaf as the replacement.
          <ClosingCatalog
            sectionLabel={match.label}
            sectionDescription={description}
          />
        ) : surface === "library" && section === "summary" ? (
          // #863's library slug-specific body, by the SAME mechanism. The
          // generic catalog offers `NewAssetDialog`, whose Pack placement field
          // is a free-text coordinate — the input the committed MOD/NII
          // equivalence contract forbids for this family, and the one that
          // cannot express the nested diagnostic target at all. It also lists
          // both Summary shapes as one undifferentiated table, when a report
          // section and one evaluation's diagnostic findings are different
          // clinical things with different runtime files.
          <SummaryCatalog
            sectionLabel={match.label}
            sectionDescription={description}
          />
        ) : surface === "library" && section === "chief-complaint" ? (
          // #492's first library slug-specific body, by the SAME mechanism the
          // clinical side already uses for `summary` and `questionnaires`. The
          // canonical Main-concern catalog lists Name · Opening prompt · Updated
          // · Adoption, and the generic catalog cannot render it: the opening
          // prompt is CONTENT, and catalog reads carry none by contract.
          <MainConcernCatalog
            sectionLabel={match.label}
            sectionDescription={description}
          />
        ) : surface === "library" && isLibrarySectionSlug(section) ? (
          <LibraryCatalog
            section={section}
            sectionLabel={match.label}
            sectionDescription={description}
          />
        ) : (
          <UnavailableState
            title={`${match.label} is not editable yet`}
            description={`${emptyHeading} The authoring surface for this section is delivered by a later ticket, so nothing here can be edited or saved today.`}
            blockedBy={`Delivered by issue ${match.owner}.`}
          />
        )}
      </div>
    </div>
  );
}
